/**
 * Speaking Annotation Parser
 *
 * Parses speaking_strategy focus/tips text (Chinese) into structured
 * visual annotations for sentence display.
 *
 * Recognized patterns:
 *   - stress:     重音放在/落在/在 'X', 強調 'X', X 要/需重讀
 *   - link:       X 的連讀, X 連讀, 形成連音, 連讀成
 *   - pause:      稍作停頓, 稍微停頓, 停頓 (mapped to comma/period before)
 *   - reduction:  弱讀, 縮讀, 輕讀
 *   - intonation: 語調下降/上揚/微升
 *   - ipa:        發音為 /X/, 發 /X/ 音, /X/ (shown as hint)
 */

// ── Types ─────────────────────────────────────────────────────

export type AnnotationType = 'stress' | 'link' | 'pause' | 'reduction' | 'intonation' | 'ipa' | 'syllables';

export interface ParsedAnnotation {
  word: string;          // The word(s) in the sentence to annotate
  type: AnnotationType;
  hint?: string;         // e.g., IPA, reduction form, syllable breakdown
}

export interface ChunkAnnotation {
  chunkText: string;
  chunkTranslation: string;
  chunkFocus: string;
  annotations: ParsedAnnotation[];
  intonation?: 'rising' | 'falling';
  pause?: boolean;       // pause after this chunk
}

export interface SentenceAnnotation {
  text: string;
  tips: string;
  chunks: ChunkAnnotation[];
  tone?: string;         // from tips_native parsing
}

interface SpeakingChunk {
  text: string;
  focus?: string;
  translation?: string;
  translations?: Record<string, string> | null;
}

interface SpeakingStrategy {
  chunks?: SpeakingChunk[];
  tips_native?: string;
  tips_natives?: Record<string, string> | null;
  tone?: string;
  pace?: string;
  emotion?: string;
}

// ── Extraction helpers ────────────────────────────────────────

/**
 * Extract quoted English words from Chinese text.
 * Matches: 'word', "word", 「word」, and bare English words near keywords.
 */
function extractQuotedWords(text: string): string[] {
  const results: string[] = [];

  // Match words in quotes: 'X', "X", 「X」, 『X』
  const quotePatterns = [
    /[''']([A-Za-z][\w\s'-]*?)[''']/g,
    /[""]([A-Za-z][\w\s'-]*?)[""]/g,
    /「([A-Za-z][\w\s'-]*?)」/g,
  ];

  for (const pattern of quotePatterns) {
    let match;
    while ((match = pattern.exec(text)) !== null) {
      results.push(match[1].trim());
    }
  }

  return results;
}

/**
 * Extract English words near a Chinese keyword within the focus text.
 * e.g., "強調 Ever" → "Ever"
 */
function extractNearbyEnglish(text: string, keyword: string): string[] {
  const results: string[] = [];
  const idx = text.indexOf(keyword);
  if (idx === -1) return results;

  // Look after the keyword
  const after = text.slice(idx + keyword.length).trim();
  const afterMatch = after.match(/^[，,、和及與\s]*([A-Za-z][\w'-]*(?:\s+[A-Za-z][\w'-]*){0,3})/);
  if (afterMatch) {
    results.push(afterMatch[1].trim());
  }

  return results;
}

/**
 * Parse stress annotations from focus text.
 * Patterns: 重音放在/落在/在 X, 強調 X, X 要/需重讀
 */
function parseStress(focus: string, chunkText: string): ParsedAnnotation[] {
  const annotations: ParsedAnnotation[] = [];
  const stressKeywords = ['重音放在', '重音落在', '重音在', '強調', '要重讀', '需重讀', '重讀'];

  for (const kw of stressKeywords) {
    if (!focus.includes(kw)) continue;

    // First try quoted words
    const quoted = extractQuotedWords(focus);
    if (quoted.length > 0) {
      for (const w of quoted) {
        // Verify the word exists in the chunk
        if (chunkText.toLowerCase().includes(w.toLowerCase())) {
          annotations.push({ word: w, type: 'stress' });
        }
      }
    }

    // Then try nearby English words
    const nearby = extractNearbyEnglish(focus, kw);
    for (const w of nearby) {
      const words = w.split(/\s+/);
      for (const single of words) {
        if (single.length < 2) continue;
        // Skip if already found via quotes
        if (annotations.some(a => a.word.toLowerCase() === single.toLowerCase())) continue;
        if (chunkText.toLowerCase().includes(single.toLowerCase())) {
          annotations.push({ word: single, type: 'stress' });
        }
      }
    }
  }

  // Also detect "X 的重音" pattern
  const emphPattern = /([A-Za-z][\w'-]*)\s*的重音/g;
  let match;
  while ((match = emphPattern.exec(focus)) !== null) {
    const w = match[1];
    if (chunkText.toLowerCase().includes(w.toLowerCase())) {
      if (!annotations.some(a => a.word.toLowerCase() === w.toLowerCase())) {
        annotations.push({ word: w, type: 'stress' });
      }
    }
  }

  return annotations;
}

/**
 * Parse link (liaison/connected speech) annotations.
 * Patterns: X 的連讀, X 連讀, 形成連音, 連讀成
 */
function parseLink(focus: string, chunkText: string): ParsedAnnotation[] {
  const annotations: ParsedAnnotation[] = [];

  if (!focus.includes('連讀') && !focus.includes('連音') && !focus.includes('連接')) return annotations;

  // Extract quoted words near 連讀/連音
  const quoted = extractQuotedWords(focus);

  // Try to find word pairs: "X 的 Y 連讀", "X 與/和 Y 的連讀"
  const linkPairPatterns = [
    /([A-Za-z][\w'-]*)\s*的\s*[/\w]*\s*音?\s*與\s*([A-Za-z][\w'-]*)\s*(?:的|之間)?\s*(?:連讀|連音)/g,
    /([A-Za-z][\w'-]*)\s*(?:與|和|跟)\s*([A-Za-z][\w'-]*)\s*(?:的|之間)?\s*(?:連讀|連音|連接)/g,
    /([A-Za-z][\w'-]*)\s+([A-Za-z][\w'-]*)\s*(?:的|需)?\s*連讀/g,
  ];

  for (const pattern of linkPairPatterns) {
    let m;
    while ((m = pattern.exec(focus)) !== null) {
      const phrase = `${m[1]} ${m[2]}`;
      if (chunkText.toLowerCase().includes(phrase.toLowerCase())) {
        annotations.push({ word: phrase, type: 'link' });
      }
    }
  }

  // Also check for quoted multi-word phrases that are about linking
  for (const q of quoted) {
    if (q.includes(' ') && chunkText.toLowerCase().includes(q.toLowerCase())) {
      if (!annotations.some(a => a.word.toLowerCase() === q.toLowerCase())) {
        annotations.push({ word: q, type: 'link' });
      }
    }
  }

  return annotations;
}

/**
 * Parse pause annotations.
 * Patterns: 稍作停頓, 稍微停頓, 停頓
 */
function parsePause(focus: string): boolean {
  return /停頓/.test(focus);
}

/**
 * Parse reduction (weak form) annotations.
 * Patterns: 弱讀, 縮讀, 輕讀
 */
function parseReduction(focus: string, chunkText: string): ParsedAnnotation[] {
  const annotations: ParsedAnnotation[] = [];

  if (!focus.includes('弱讀') && !focus.includes('縮讀') && !focus.includes('輕讀')) return annotations;

  const quoted = extractQuotedWords(focus);
  for (const w of quoted) {
    if (chunkText.toLowerCase().includes(w.toLowerCase())) {
      // Try to find the reduced form: "縮讀為 X" or "→ X"
      const hintMatch = focus.match(/(?:縮讀為|讀作|→)\s*([A-Za-z][\w'''-]*(?:\s+[A-Za-z][\w'''-]*){0,2})/);
      annotations.push({
        word: w,
        type: 'reduction',
        hint: hintMatch ? hintMatch[1] : undefined,
      });
    }
  }

  // Try nearby English words if no quoted words found
  if (annotations.length === 0) {
    for (const kw of ['弱讀', '縮讀', '輕讀']) {
      const nearby = extractNearbyEnglish(focus, kw);
      for (const w of nearby) {
        if (chunkText.toLowerCase().includes(w.toLowerCase())) {
          annotations.push({ word: w, type: 'reduction' });
        }
      }
    }
  }

  return annotations;
}

/**
 * Parse intonation annotations.
 * Patterns: 語調下降, 語調上揚, 語調微升
 */
function parseIntonation(focus: string): 'rising' | 'falling' | undefined {
  if (/語調.*(?:下降|降低)/.test(focus)) return 'falling';
  if (/語調.*(?:上揚|上升|微升)/.test(focus)) return 'rising';
  return undefined;
}

/**
 * Parse IPA / pronunciation hints.
 * Patterns: /X/, 發音為 /X/, 發 /X/ 音
 */
function parseIPA(focus: string, chunkText: string): ParsedAnnotation[] {
  const annotations: ParsedAnnotation[] = [];

  // Find IPA notations: /ˈæn.əlz/
  const ipaPattern = /([A-Za-z][\w'-]*)\s*的\s*發音\s*(?:為\s*)?([/][^/]+[/])/g;
  let match;
  while ((match = ipaPattern.exec(focus)) !== null) {
    if (chunkText.toLowerCase().includes(match[1].toLowerCase())) {
      annotations.push({ word: match[1], type: 'ipa', hint: match[2] });
    }
  }

  // Pattern: 發音 /X/  with word before
  const ipaPattern2 = /([A-Za-z][\w'-]*)\s*.*?([/][^/]+[/])/g;
  while ((match = ipaPattern2.exec(focus)) !== null) {
    const word = match[1];
    const ipa = match[2];
    if (chunkText.toLowerCase().includes(word.toLowerCase())) {
      if (!annotations.some(a => a.word.toLowerCase() === word.toLowerCase())) {
        annotations.push({ word, type: 'ipa', hint: ipa });
      }
    }
  }

  return annotations;
}

/**
 * Parse -ed pronunciation hints.
 * Patterns: -ed 發 /t/ 音, 結尾 -ed 發 /d/ 音
 */
function parseEdPronunciation(focus: string, chunkText: string): ParsedAnnotation[] {
  const annotations: ParsedAnnotation[] = [];

  // Pattern: X 的/字尾的 -ed 發 /X/ 音
  const edPattern = /([A-Za-z][\w'-]*)\s*(?:的|字尾的|結尾的?)?\s*(?:-ed|ed)\s*發\s*([/][^/]+[/])\s*(?:的)?音/g;
  let match;
  while ((match = edPattern.exec(focus)) !== null) {
    if (chunkText.toLowerCase().includes(match[1].toLowerCase())) {
      annotations.push({ word: match[1], type: 'ipa', hint: `-ed → ${match[2]}` });
    }
  }

  return annotations;
}

// ── Main parser ───────────────────────────────────────────────

/**
 * Parse a single chunk's focus text into annotations.
 */
function parseChunkFocus(chunk: SpeakingChunk): ChunkAnnotation {
  const focus = chunk.focus || '';
  const text = chunk.text || '';
  const annotations: ParsedAnnotation[] = [];

  // Stress
  annotations.push(...parseStress(focus, text));

  // Link
  annotations.push(...parseLink(focus, text));

  // Reduction
  annotations.push(...parseReduction(focus, text));

  // IPA
  annotations.push(...parseIPA(focus, text));

  // -ed pronunciation
  annotations.push(...parseEdPronunciation(focus, text));

  // Intonation (chunk-level)
  const intonation = parseIntonation(focus);

  // Pause (chunk-level)
  const pause = parsePause(focus);

  return {
    chunkText: text,
    chunkTranslation: chunk.translation || '',
    chunkFocus: focus,
    annotations,
    intonation,
    pause: pause || undefined,
  };
}

/**
 * Parse tone from tips_native text.
 */
function parseTone(tips: string): string | undefined {
  // Look for tone/emotion keywords
  const tonePatterns: [RegExp, string][] = [
    [/語氣.*?堅定/, 'Firm'],
    [/語氣.*?正式/, 'Formal'],
    [/語氣.*?自信/, 'Confident'],
    [/語氣.*?沉穩/, 'Composed'],
    [/語氣.*?讚賞/, 'Appreciative'],
    [/語氣.*?平穩/, 'Steady'],
    [/新聞報導/, 'News report'],
    [/正式.*?新聞/, 'Formal news'],
    [/自嘲/, 'Self-deprecating'],
    [/安慰|安撫/, 'Reassuring'],
    [/後悔|遺憾/, 'Regretful'],
    [/驚訝|驚嘆/, 'Surprised'],
    [/對比/, 'Contrasting'],
    [/假設/, 'Hypothetical'],
  ];

  for (const [pattern, tone] of tonePatterns) {
    if (pattern.test(tips)) return tone;
  }
  return undefined;
}

/**
 * Parse an entire sentence's speaking strategy into visual annotations.
 */
export function parseSpeakingStrategy(
  sentenceText: string,
  strategy: SpeakingStrategy | null | undefined
): SentenceAnnotation | null {
  if (!strategy?.chunks || strategy.chunks.length === 0) return null;

  const tips = strategy.tips_native || '';
  const chunks = strategy.chunks.map(chunk => parseChunkFocus(chunk));
  const tone = strategy.tone || parseTone(tips);

  return {
    text: sentenceText,
    tips,
    chunks,
    tone,
  };
}

/**
 * Debug: parse and return a human-readable summary.
 */
export function debugParseSentence(
  sentenceText: string,
  strategy: SpeakingStrategy | null | undefined
): string {
  const result = parseSpeakingStrategy(sentenceText, strategy);
  if (!result) return `[No strategy data]\n${sentenceText}\n`;

  const lines: string[] = [];
  lines.push(`📝 ${sentenceText}`);
  if (result.tone) lines.push(`   🎭 Tone: ${result.tone}`);

  for (const chunk of result.chunks) {
    lines.push(`   📦 "${chunk.chunkText}"`);
    if (chunk.intonation) lines.push(`      ↕ Intonation: ${chunk.intonation}`);
    if (chunk.pause) lines.push(`      ⏸ Pause after`);
    for (const ann of chunk.annotations) {
      const hint = ann.hint ? ` (${ann.hint})` : '';
      lines.push(`      ${getAnnotationIcon(ann.type)} ${ann.type}: "${ann.word}"${hint}`);
    }
    if (chunk.annotations.length === 0) {
      lines.push(`      ⚪ (no annotations extracted)`);
    }
  }

  return lines.join('\n');
}

function getAnnotationIcon(type: AnnotationType): string {
  switch (type) {
    case 'stress': return '🔴';
    case 'link': return '🔗';
    case 'pause': return '⏸';
    case 'reduction': return '🔻';
    case 'intonation': return '↕';
    case 'ipa': return '🔤';
    case 'syllables': return '📊';
    default: return '⚪';
  }
}
